import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { TenantPlanErrorModalComponent } from '../components/tenant-plan-error-modal/tenant-plan-error-modal.component';
import { EmailTemplateType } from '../models/email-templates/email-template-type';
import { EmailTemplate } from '../models/email-templates/emailTemplate';
import { MandrillEmailTemplateType } from '../models/email-templates/mandrill-email-template-type';
import { EmailCustomization, EmailScenario } from '../models/emailCustomization';
import { EmailType } from '../models/emailMessage';
import { ButtonTexts } from '../models/mandrillEmailTemplate';
import { Profile } from '../models/profile';
import { EmailRecipient } from '../models/team/emailRecipient';
import { Team } from '../models/team/team';
import { AppConfigService } from '../services/app-config.service';
import { EmailTemplateService } from '../services/email-template.service';
import { AttributeService } from '../services/attribute.service';
import { AttributeEntityType } from '../models/attributes/attributeEntityType';

@Component({
  selector: 'app-compose-email',
  templateUrl: './compose-email.component.html',
  styleUrls: ['./compose-email.component.scss'],
  standalone: false
})
export class ComposeEmailComponent implements OnInit, OnChanges {
  static readonly TeamLevelCustomizationTemplateName: string = "This team's invitation";
  static readonly TeamLevelCustomizationReminderTemplateName: string = "This team's reminder";

  static readonly EmailTemplateFormControlName: string = 'emailTemplate';
  static readonly FromNameFormControlName: string = 'fromName';
  static readonly ReplyToFormControlName: string = 'replyTo';
  static readonly SubjectFormControlName: string = 'subject';
  static readonly ContentFormControlName: string = 'content';

  emailForm: UntypedFormGroup;
  profile: Profile;
  emailTemplates: EmailTemplate[];
  attributes: any[] = [];
  generalEmailTemplate: boolean = false;

  private isAuthenticated: boolean;
  private subscriptions: Subscription[] = [];

  private get emailCustomization(): EmailCustomization {
    return this.team?.invitationEmailCustomization(EmailScenario.Default);
  }
  private get emailTemplateFormField() {
    return this.emailForm.get(ComposeEmailComponent.EmailTemplateFormControlName);
  }
  private get contactNameFormField() {
    return this.emailForm.get(ComposeEmailComponent.FromNameFormControlName);
  }
  private get contactEmailFormField() {
    return this.emailForm.get(ComposeEmailComponent.ReplyToFormControlName);
  }
  private get subjectFormField() {
    return this.emailForm.get(ComposeEmailComponent.SubjectFormControlName);
  }
  private get contentFormField() {
    return this.emailForm.get(ComposeEmailComponent.ContentFormControlName);
  }

  public get isEmailTypeInvitee(): boolean {
    return this.emailType == EmailType.Invitee;
  }

  public get isEmailTypeInvitees(): boolean {
    return this.emailType == EmailType.Invitees;
  }

  public get isEmailTypeTeamMembers(): boolean {
    return this.emailType == EmailType.TeamMembers;
  }

  public get isEmailTypeSurvey(): boolean {
    return this.emailType == EmailType.Survey;
  }

  public get isEmailTypeSurveyReminder(): boolean {
    return this.emailType == EmailType.SurveyReminder;
  }

  public get isEmailTypeEvaluationReminder(): boolean {
    return this.emailType == EmailType.EvaluationReminder;
  }

  public get isEmailTypeTeamFormReminder(): boolean {
    return this.emailType == EmailType.TeamFormReminder;
  }

  public get isEmailTypeTeamForm(): boolean {
    return this.emailType == EmailType.TeamForm;
  }

  public get isEmailTypeSingle(): boolean {
    return this.emailType == EmailType.Single;
  }

  public get isEmailTypeBulk(): boolean {
    return this.emailType == EmailType.Bulk;
  }

  public get isEmailTypeInvitationReminder(): boolean {
    return this.emailType == EmailType.TeamInvitationReminder;
  }
  public get isEmailTypeWallCertificate(): boolean {
    return this.emailType == EmailType.WallCertificate;
  }

  public get isEmailTypePortalInvitation(): boolean {
    return this.emailType == EmailType.PortalInvitation;
  }

  @Input() emailType: EmailType;
  @Input() team: Team;
  @Input() recipients: Array<EmailRecipient>;
  @Input() recipientPersonId: string;
  @Input() emailCustomizationInput: EmailCustomization;
  @Output() composedEmailForm = new EventEmitter<UntypedFormGroup>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    public authService: AuthService,
    private emailTemplateService: EmailTemplateService,
    private appConfigService: AppConfigService,
    private dialog: MatDialog,
    private attributeService: AttributeService
  ) {}

  ngOnInit(): void {
    this.attributeService.attributes.subscribe((attributes) => {
      if (!attributes) return;
      this.attributes = attributes.filter(
        (x) =>
          x.entityType != AttributeEntityType.Survey &&
          x.entityType != AttributeEntityType.Evaluation
      );
    });
    if (this.isEmailTypeInvitees || this.isEmailTypeInvitee) {
      this.initInviteeEmails();
    } else if (this.isEmailTypeTeamMembers) {
      this.initTeamMembersEmails();
    } else if (this.isEmailTypeSurvey) {
      this.initSurveyEmails();
    } else if (this.isEmailTypeTeamForm) {
      this.initTeamFormEmails();
    } else if (this.isEmailTypeInvitationReminder) {
      this.initInvitationEmails();
    } else if (
      this.isEmailTypeSurveyReminder ||
      this.isEmailTypeEvaluationReminder ||
      this.isEmailTypeTeamFormReminder
    ) {
      this.initReminderEmail();
    } else if (
      this.isEmailTypeBulk ||
      this.isEmailTypeSingle ||
      this.isEmailTypeWallCertificate ||
      this.isEmailTypePortalInvitation
    ) {
      this.attributes = this.attributes.filter((x) => x.entityType === AttributeEntityType.Person);
      this.initGeneralEmails();
      this.generalEmailTemplate = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['emailCustomizationInput'] && !changes['emailCustomizationInput'].firstChange) {
      this.updateFormWithEmailCustomization();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  getTemplateName() {
    if (
      this.isEmailTypeInvitee ||
      this.isEmailTypeInvitees ||
      this.isEmailTypeTeamMembers ||
      this.isEmailTypeSingle ||
      this.isEmailTypeBulk ||
      this.isEmailTypeInvitationReminder ||
      this.isEmailTypeWallCertificate ||
      this.isEmailTypePortalInvitation
    ) {
      return MandrillEmailTemplateType.GenericEmailTemplate;
    } else if (this.isEmailTypeSurvey || this.isEmailTypeSurveyReminder) {
      return MandrillEmailTemplateType.SurveyInvitation;
    } else if (this.isEmailTypeEvaluationReminder) {
      return MandrillEmailTemplateType.EvaluationInvitationCustom;
    } else if (this.isEmailTypeTeamFormReminder) {
      return MandrillEmailTemplateType.SurveyInvitation;
    } else if (this.isEmailTypeTeamForm) {
      return MandrillEmailTemplateType.SurveyInvitation; // fix: ask Kenji about mandrill template
    }
    return null;
  }

  getButtonText(): string {
    if (this.isEmailTypeInvitee || this.isEmailTypeInvitees || this.isEmailTypeInvitationReminder) {
      return ButtonTexts.TeamInvitation;
    }
    if (this.isEmailTypePortalInvitation) {
      return ButtonTexts.InvitationToPortal;
    }

    return null;
  }

  getButtonLink(): string {
    if (
      this.isEmailTypeInvitee ||
      this.isEmailTypeInvitees ||
      this.isEmailTypeInvitationReminder ||
      this.isEmailTypePortalInvitation
    ) {
      return '#';
    }
    return null;
  }

  stepComposeEmailValid(): boolean {
    if (!this.emailForm) return false;
    this.composedEmailForm.emit(this.emailForm);
    return this.emailForm.valid;
  }

  setEmailTemplate(
    emailTemplateType: EmailTemplateType,
    emailScenario: EmailScenario,
    emailCustomization: EmailCustomization,
    teamLevelCustomizationTemplateName: string,
    preSelectTemplate: boolean
  ): void {
    this.subscriptions.push(
      this.emailTemplateService.emailTemplates.subscribe((templates) => {
        if (emailTemplateType == 'TeamGroupEmail' && emailScenario == 'TeamEmail') {
          this.emailTemplates = templates.filter(
            (t) =>
              (t.templateType === emailTemplateType && t.scenario === emailScenario) ||
              (t.templateType === 'GeneralEmail' && t.scenario === 'GeneralEmail')
          );
        } else {
          this.emailTemplates = templates.filter(
            (t) => t.templateType === emailTemplateType && t.scenario === emailScenario
          );
        }
        // if we have a team-level email customization, add to drop-down with a null ID, and preselect this item
        if (emailCustomization) {
          this.emailTemplates.push(
            new EmailTemplate(
              null,
              emailCustomization?.scenario,
              teamLevelCustomizationTemplateName,
              EmailTemplateType.TeamInvitation,
              emailCustomization?.subject,
              emailCustomization?.content,
              emailCustomization?.contactName,
              emailCustomization?.contactEmail,
              false
            )
          );
          this.emailTemplateFormField.setValue(
            this.emailTemplates.filter(
              (x) => !x.id && x.templateName === teamLevelCustomizationTemplateName
            )[0]
          );
        }

        // preselect email template, if email customization is email template based and not already pre-selected
        if (
          preSelectTemplate &&
          !this.emailTemplateFormField.value &&
          emailCustomization?.emailTemplate
        ) {
          this.emailTemplateFormField.setValue(
            this.emailTemplates.filter((x) => x.id === emailCustomization.emailTemplate.id)[0]
          );
        }
      })
    );
  }

  checkAuthenticationAndSetProfile(): void {
    this.authService.hasBeenAuthenticated().subscribe((hasBeenAuthenticated) => {
      if (hasBeenAuthenticated) {
        this.isAuthenticated = true;
        this.setProfile();
      }
    });
  }

  emailTemplateSelected(): void {
    const emailTemplate: EmailTemplate = this.emailTemplateFormField.value;
    if (emailTemplate) {
      this.contactNameFormField.setValue(emailTemplate.contactName);
      this.contactEmailFormField.setValue(emailTemplate.contactEmail);
      this.subjectFormField.setValue(emailTemplate.subject);
      this.contentFormField.setValue(emailTemplate.content);
      if (this.isEmailTypeInvitees) {
        // in case we don't have the from or reply-to, use logged-in user's info
        if (this.isAuthenticated) {
          this.setProfile();
        }
      }
    }
  }

  setProfile() {
    const comp = this;
    comp.authService.getProfile().subscribe((profile) => {
      comp.profile = profile;
      if (!comp.emailForm.get(ComposeEmailComponent.FromNameFormControlName).value) {
        comp.emailForm
          .get(ComposeEmailComponent.FromNameFormControlName)
          .setValue(profile.full_name);
      }
      if (!comp.emailForm.get(ComposeEmailComponent.ReplyToFormControlName).value) {
        comp.emailForm.get(ComposeEmailComponent.ReplyToFormControlName).setValue(profile.email);
      }
    });
  }

  buildEmailForm() {
    if (this.isEmailTypeInvitees) {
      this.emailForm = this.formBuilder.group({
        emailTemplate: [],
        fromName: [this.emailCustomization.contactName],
        replyTo: [this.emailCustomization.contactEmail],
        subject: [this.emailCustomization.subject, [Validators.required]],
        content: [this.emailCustomization.content, [Validators.required]],
        scheduleReminder: [false],
        reminderDaysPreset: ['7'],
        reminderDaysCustom: [null, [Validators.min(1)]]
      });
    } else if (this.isEmailTypeInvitee) {
      this.emailForm = this.formBuilder.group(
        {
          emailTemplate: [],
          fromName: [this.emailCustomization?.contactName],
          replyTo: [this.emailCustomization?.contactEmail],
          subject: [this.emailCustomization?.subject, [Validators.required]],
          content: [this.emailCustomization?.content, [Validators.required]],
          scheduleReminder: [false],
          reminderDaysPreset: ['7'],
          reminderDaysCustom: [null, [Validators.min(1)]]
        },
        {
          validators: [ComposeEmailComponent.isWholeNumber]
        }
      );
    } else if (
      this.isEmailTypeTeamForm ||
      this.isEmailTypeSurvey ||
      this.isEmailTypeSurveyReminder ||
      this.isEmailTypeEvaluationReminder ||
      this.isEmailTypeTeamFormReminder
    ) {
      this.emailForm = this.formBuilder.group({
        emailTemplate: [],
        fromName: [this.emailCustomizationInput?.contactName, [Validators.required]],
        replyTo: [this.emailCustomizationInput?.contactEmail, [Validators.required]],
        subject: [this.emailCustomizationInput?.subject, [Validators.required]],
        content: [this.emailCustomizationInput?.content, [Validators.required]]
      });
    } else {
      this.emailForm = this.formBuilder.group({
        emailTemplate: [],
        fromName: [null, [Validators.required]],
        replyTo: [null, [Validators.required]],
        subject: [null, [Validators.required]],
        content: [null, [Validators.required]]
      });
    }
  }

  private initInviteeEmails() {
    this.setEmailTemplate(
      EmailTemplateType.TeamInvitation,
      this.emailCustomization.scenario,
      this.emailCustomization,
      ComposeEmailComponent.TeamLevelCustomizationTemplateName,
      true
    );

    this.emailTemplateService.getAll();
    this.buildEmailForm();
  }

  private initTeamMembersEmails() {
    this.setEmailTemplate(
      EmailTemplateType.TeamGroupEmail,
      EmailScenario.TeamEmail,
      null,
      null,
      false
    );

    this.emailTemplateService.getAll();
    this.buildEmailForm();
    this.checkAuthenticationAndSetProfile();
  }

  private initSurveyEmails() {
    this.buildEmailForm();
    this.checkAuthenticationAndSetProfile();
  }

  private initReminderEmail() {
    this.buildEmailForm();
    const existingSubject = this.emailForm.get(ComposeEmailComponent.SubjectFormControlName).value;
    this.emailForm
      .get(ComposeEmailComponent.SubjectFormControlName)
      .setValue('Reminder: ' + (existingSubject || ''));
    this.checkAuthenticationAndSetProfile();
  }

  private initTeamFormEmails() {
    this.buildEmailForm();
    this.checkAuthenticationAndSetProfile();
  }

  private initInvitationEmails() {
    this.buildEmailForm();
    if (this.isEmailTypeInvitationReminder) {
      let emailTemplateType =
        this.emailType === EmailType.TeamInvitationReminder
          ? EmailTemplateType.TeamInvitation
          : EmailTemplateType.TeamGroupEmail;
      let emailScenario =
        this.emailType === EmailType.TeamInvitationReminder
          ? EmailScenario.Reminder
          : EmailScenario.TeamEmail;
      this.setEmailTemplate(
        emailTemplateType,
        emailScenario,
        this.emailCustomizationInput,
        ComposeEmailComponent.TeamLevelCustomizationReminderTemplateName,
        false
      );

      this.emailTemplateService.getAll();
    }

    this.emailForm
      .get(ComposeEmailComponent.SubjectFormControlName)
      .setValue(this.emailCustomizationInput?.subject);
    this.emailForm
      .get(ComposeEmailComponent.ContentFormControlName)
      .setValue(this.emailCustomizationInput?.content);
    this.emailForm
      .get(ComposeEmailComponent.FromNameFormControlName)
      .setValue(this.emailCustomizationInput?.contactName);
    this.emailForm
      .get(ComposeEmailComponent.ReplyToFormControlName)
      .setValue(this.emailCustomizationInput?.contactEmail);
    this.checkAuthenticationAndSetProfile();
  }

  private initGeneralEmails() {
    this.buildEmailForm();
    this.setEmailTemplate(
      EmailTemplateType.GeneralEmail,
      EmailScenario.GeneralEmail,
      null,
      null,
      false
    );
    this.emailTemplateService.getAll();
    this.checkAuthenticationAndSetProfile();
  }

  get canScheduleReminders(): boolean {
    return this.appConfigService.tenant?.plan?.canScheduleEmailReminders;
  }

  showPlanUpgradePrompt(): void {
    if (!this.canScheduleReminders) {
      TenantPlanErrorModalComponent.openDialog(
        this.dialog,
        'Scheduled reminders are not available on your plan.',
        `You'll be able to send a reminder email to anyone who has not responded within a set number of days. To use this feature, you'll need to upgrade your Lineup plan.`,
        true
      );
    }
  }

  private static isWholeNumber = (formGroup: UntypedFormGroup): ValidationErrors | null => {
    const scheduleReminder = formGroup.get('scheduleReminder');
    const reminderDaysPreset = formGroup.get('reminderDaysPreset');
    const reminderDaysCustom = formGroup.get('reminderDaysCustom');
    return scheduleReminder.value &&
      reminderDaysPreset.value === 'custom' &&
      !Number.isSafeInteger(reminderDaysCustom.value)
      ? { wholeNumber: true }
      : null;
  };

  private updateFormWithEmailCustomization(): void {
    if (this.emailForm && this.emailCustomizationInput) {
      this.emailForm.patchValue({
        fromName: this.emailCustomizationInput.contactName,
        replyTo: this.emailCustomizationInput.contactEmail,
        subject: this.emailCustomizationInput.subject,
        content: this.emailCustomizationInput.content
      });
    }
  }
}
